import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Monitor, ScanLine, Printer, Archive, Tag, Tablet, CreditCard,
    Wifi, CheckCircle, Circle, AlertCircle, ChevronRight, ChevronLeft,
    RefreshCw, Download, PrinterCheck, ShoppingCart
} from 'lucide-react';

const HARDWARE_ITEMS = [
    {
        type: 'touchscreen',
        name: 'Touchscreen Terminal',
        description: 'Main POS terminal with touch display',
        icon: Monitor,
        isRequired: true,
        testSteps: ['Power on terminal', 'Test touch responsiveness', 'Verify display clarity']
    },
    {
        type: 'scanner',
        name: 'Barcode Scanner',
        description: 'USB or Bluetooth barcode scanner',
        icon: ScanLine,
        isRequired: true,
        testSteps: ['Connect scanner', 'Scan test barcode', 'Verify beep and input']
    },
    {
        type: 'receipt_printer',
        name: 'Receipt Printer',
        description: 'Thermal receipt printer',
        icon: Printer,
        isRequired: true,
        testSteps: ['Load paper roll', 'Connect to terminal', 'Print test receipt']
    },
    {
        type: 'cash_drawer',
        name: 'Cash Drawer',
        description: 'Cash drawer connected to receipt printer',
        icon: Archive,
        isRequired: true,
        testSteps: ['Connect to printer', 'Test open command', 'Verify lock mechanism']
    },
    {
        type: 'label_printer',
        name: 'Label Printer',
        description: 'For printing product labels',
        icon: Tag,
        isRequired: false,
        testSteps: ['Load label roll', 'Connect to terminal', 'Print test label']
    },
    {
        type: 'customer_display',
        name: 'Customer Display',
        description: 'Secondary display for customers',
        icon: Monitor,
        isRequired: false,
        testSteps: ['Connect display', 'Test mirroring', 'Verify visibility']
    },
    {
        type: 'tablet',
        name: 'Tablet/Backup Device',
        description: 'Backup POS device or mobile checkout',
        icon: Tablet,
        isRequired: false,
        testSteps: ['Install POS app', 'Connect to WiFi', 'Test sync with main terminal']
    },
    {
        type: 'card_reader',
        name: 'Card Reader',
        description: 'EMV chip and contactless card reader',
        icon: CreditCard,
        isRequired: true,
        testSteps: ['Connect reader', 'Test chip card', 'Test contactless payment']
    },
    {
        type: 'network',
        name: 'Network/Internet',
        description: 'Stable internet connection',
        icon: Wifi,
        isRequired: true,
        testSteps: ['Verify WiFi connection', 'Test speed', 'Check backup connection']
    },
    {
        type: 'test_transaction',
        name: 'Test Transaction',
        description: 'Complete a test sale transaction',
        icon: ShoppingCart,
        isRequired: true,
        testSteps: ['Add item to cart', 'Process payment', 'Print receipt', 'Verify cash drawer opens']
    },
    {
        type: 'receipt_test',
        name: 'Receipt Test',
        description: 'Verify receipt printing and formatting',
        icon: PrinterCheck,
        isRequired: true,
        testSteps: ['Print test receipt', 'Check formatting', 'Verify logo/address prints']
    },
    {
        type: 'drawer_test',
        name: 'Cash Drawer Test',
        description: 'Test cash drawer functionality',
        icon: Archive,
        isRequired: true,
        testSteps: ['Test manual open', 'Test auto-open on cash sale', 'Verify close latch']
    }
];

const HardwareSetup = () => {
    const { user } = useAuth();
    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadChecklist = async () => {
        try {
            setLoading(true);
            const response = await api.get('/setup/hardware/checklist', {
                params: { store_id: user?.store_id }
            });
            setChecklist(response.data);
        } catch (error) {
            if (error.response?.status === 404) {
                // Create checklist if doesn't exist
                await createChecklist();
            } else {
                toast.error('Failed to load hardware checklist');
            }
        } finally {
            setLoading(false);
        }
    };

    const createChecklist = async () => {
        try {
            const defaultItems = HARDWARE_ITEMS.map(item => ({
                item_type: item.type,
                name: item.name,
                is_installed: false,
                is_tested: false,
                notes: '',
                installed_at: null,
                tested_at: null
            }));

            await api.post('/setup/hardware/checklist/create', {
                store_id: user?.store_id,
                items: defaultItems
            });

            // Reload after creation
            const response = await api.get('/setup/hardware/checklist', {
                params: { store_id: user?.store_id }
            });
            setChecklist(response.data);
        } catch (error) {
            toast.error('Failed to create hardware checklist');
        }
    };

    const updateItemStatus = async (itemType, updates) => {
        try {
            await api.put(`/setup/hardware/checklist/${itemType}`, {
                store_id: user?.store_id,
                ...updates
            });

            // Reload checklist
            const response = await api.get('/setup/hardware/checklist', {
                params: { store_id: user?.store_id }
            });
            setChecklist(response.data);
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleInstall = (itemType) => {
        updateItemStatus(itemType, {
            is_installed: true,
            installed_at: new Date().toISOString()
        });
    };

    const handleTest = (itemType) => {
        updateItemStatus(itemType, {
            is_tested: true,
            tested_at: new Date().toISOString()
        });
    };

    const handleSaveNotes = (itemType) => {
        updateItemStatus(itemType, { notes });
        setSelectedItem(null);
        setNotes('');
    };

    const getProgress = () => {
        if (!checklist?.items?.length) return { installed: 0, tested: 0, total: 0 };
        const installed = checklist.items.filter(i => i.is_installed).length;
        const tested = checklist.items.filter(i => i.is_tested).length;
        return { installed, tested, total: checklist.items.length };
    };

    const progress = getProgress();

    const getItemStatus = (item) => {
        if (item.is_tested) return { label: 'Tested', color: 'bg-emerald-500/15 text-emerald-400', icon: CheckCircle };
        if (item.is_installed) return { label: 'Installed', color: 'bg-blue-500/15 text-blue-400', icon: Circle };
        return { label: 'Pending', color: 'bg-amber-500/15 text-amber-400', icon: AlertCircle };
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Hardware Setup Checklist</h1>
                        <p className="text-muted-foreground">Track your POS hardware installation and testing</p>
                    </div>
                    <Button variant="outline" onClick={loadChecklist} className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                </div>

                {/* Progress Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="stat-card border-l-4 border-blue-500">
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="stat-card-icon bg-blue-500/10">
                                    <Monitor className="h-5 w-5 text-blue-400" />
                                </div>
                            </div>
                            <div className="text-3xl font-black font-mono tracking-tight">
                                {progress.installed}/{progress.total}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 font-medium">Items Installed</p>
                        </CardContent>
                    </Card>

                    <Card className="stat-card border-l-4 border-emerald-500">
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="stat-card-icon bg-emerald-500/10">
                                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                                </div>
                            </div>
                            <div className="text-3xl font-black font-mono tracking-tight">
                                {progress.tested}/{progress.total}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 font-medium">Items Tested</p>
                        </CardContent>
                    </Card>

                    <Card className="stat-card border-l-4 border-purple-500">
                        <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="stat-card-icon bg-purple-500/10">
                                    <AlertCircle className="h-5 w-5 text-purple-400" />
                                </div>
                            </div>
                            <div className="text-3xl font-black font-mono tracking-tight">
                                {Math.round((progress.tested / progress.total) * 100) || 0}%
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 font-medium">Setup Complete</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Overall Progress Bar */}
                <Card className="shadow-sm">
                    <CardContent className="p-4">
                        <div className="flex justify-between text-sm mb-2">
                            <span className="font-medium">Overall Progress</span>
                            <span className="text-muted-foreground">{progress.tested} of {progress.total} items tested</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(progress.tested / progress.total) * 100}%` }}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Hardware Items List */}
                <Card className="shadow-sm">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <Monitor className="h-4 w-4 text-cyan-400" />
                            Hardware Items
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-border">
                            {HARDWARE_ITEMS.map((hardware) => {
                                const item = checklist?.items?.find(i => i.item_type === hardware.type);
                                const status = getItemStatus(item || {});
                                const StatusIcon = status.icon;
                                const HardwareIcon = hardware.icon;

                                return (
                                    <div key={hardware.type} className="p-4 hover:bg-muted/30 transition-colors">
                                        <div className="flex items-start gap-4">
                                            {/* Icon */}
                                            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                                <HardwareIcon className="h-5 w-5 text-muted-foreground" />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold">{hardware.name}</h3>
                                                    {hardware.isRequired && (
                                                        <Badge className="bg-red-500/15 text-red-400 border-0 text-xs">Required</Badge>
                                                    )}
                                                    <Badge className={`${status.color} border-0 text-xs gap-1`}>
                                                        <StatusIcon className="h-3 w-3" />
                                                        {status.label}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-2">{hardware.description}</p>

                                                {/* Test Steps */}
                                                {item?.is_installed && !item?.is_tested && (
                                                    <div className="bg-muted/50 rounded-lg p-3 mb-3">
                                                        <p className="text-xs font-medium mb-2">Test Steps:</p>
                                                        <ul className="text-xs text-muted-foreground space-y-1">
                                                            {hardware.testSteps.map((step, idx) => (
                                                                <li key={idx} className="flex items-center gap-2">
                                                                    <Circle className="h-2 w-2" />
                                                                    {step}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Notes */}
                                                {item?.notes && (
                                                    <p className="text-xs text-muted-foreground italic">
                                                        Note: {item.notes}
                                                    </p>
                                                )}

                                                {/* Actions */}
                                                <div className="flex gap-2 mt-3">
                                                    {!item?.is_installed ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleInstall(hardware.type)}
                                                        >
                                                            Mark Installed
                                                        </Button>
                                                    ) : !item?.is_tested ? (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                                                onClick={() => handleTest(hardware.type)}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-1" />
                                                                Mark Tested
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => {
                                                                    setSelectedItem(hardware.type);
                                                                    setNotes(item?.notes || '');
                                                                }}
                                                            >
                                                                Add Notes
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                setSelectedItem(hardware.type);
                                                                setNotes(item?.notes || '');
                                                            }}
                                                        >
                                                            Edit Notes
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Notes Modal */}
                {selectedItem && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <Card className="w-full max-w-md">
                            <CardHeader>
                                <CardTitle>Add Notes</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Enter notes about this hardware item..."
                                    className="w-full bg-background border border-input rounded-lg px-3 py-2 h-32 resize-none"
                                />
                                <div className="flex justify-end gap-3">
                                    <Button variant="outline" onClick={() => {
                                        setSelectedItem(null);
                                        setNotes('');
                                    }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={() => handleSaveNotes(selectedItem)}>
                                        Save Notes
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Completion Message */}
                {progress.tested === progress.total && progress.total > 0 && (
                    <Card className="shadow-sm border-emerald-500/30 bg-emerald-500/5">
                        <CardContent className="p-6 text-center">
                            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold mb-2">Hardware Setup Complete!</h2>
                            <p className="text-muted-foreground mb-4">
                                All required hardware has been installed and tested. You're ready to start using MezgebPOS!
                            </p>
                            <Button onClick={() => window.print()} className="gap-2">
                                <Download className="h-4 w-4" />
                                Print Checklist Report
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default HardwareSetup;
